import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import {
  Package,
  FileText,
  ArrowLeft,
  Building2,
  Calendar,
  Hash,
  IndianRupee,
  CheckCircle,
  XCircle,
  Info,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = "https://nissi-india.com/api";

const PurchaseDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [requestDetails, setRequestDetails] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [poDetails, setPoDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  const { requestId, itemId } = location.state || {};

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        const response = await axios.get(
          `${API}/getPendingPurchaseRequestDetailsByID?requestid=${requestId}`,
          { headers: commonHeaders }
        );

        const data = response.data?.data || response.data?.rows || null;
        setRequestDetails(data);

        const row = Array.isArray(data) ? data[0] : data;
        const approved =
          row?.fld_IsApproved === "Y" ||
          row?.fld_isApproved === "Y" ||
          row?.IsApproved === "Y";
        setIsApproved(!!approved);
      } catch (error) {
        console.error("Failed to fetch purchase request details:", error);
      }
    };

    const fetchItemDetails = async () => {
      try {
        if (requestId && itemId) {
          const response = await axios.get(
            `${API}/getPendingPurchaseItemDetails?purrequestid=${requestId}&requestitemid=${itemId}`,
            { headers: commonHeaders }
          );

          const data = response.data;
          if (data) {
            setItemDetails(data.itemDetails?.[0] || null);
            setPoDetails(data.poDetails || []);
          }
        }
      } catch (error) {
        console.error("Failed to fetch item details:", error);
        toast.error("Failed to load item details");
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequestDetails();
      fetchItemDetails();
    } else {
      setLoading(false);
    }
  }, [requestId, itemId, commonHeaders]);

  const handleApprove = async () => {
    if (!requestId) {
      toast.error("Request ID missing");
      return;
    }
    if (isApproved) {
      toast.info("This purchase request is already approved");
      return;
    }

    try {
      setApproving(true);
      const res = await axios.post(
        `${API}/purchaseRequestApproval/approve`,
        { ids: [Number(requestId)] },
        { headers: commonHeaders }
      );

      if (res.data?.success) {
        toast.success(res.data.message || "Purchase request approved successfully");
        setIsApproved(true);
      } else {
        toast.error(res.data?.message || "Approve failed");
      }
    } catch (err) {
      console.error("Approve error:", err);
      toast.error(err.response?.data?.message || "Approve failed");
    } finally {
      setApproving(false);
    }
  };

  const StatusBadge = ({ value, label }) => {
    const isYes =
      value === true ||
      value === "Yes" ||
      value === "Y" ||
      value === 1 ||
      String(value).toLowerCase() === "yes";

    return (
      <div
        className={`flex items-center justify-between p-3 rounded-xl border ${
          isDark
            ? "bg-slate-800/40 border-slate-700"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
          {label}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
            isYes ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"
          }`}
        >
          {isYes ? <CheckCircle size={12} /> : <XCircle size={12} />}
          {isYes ? "Yes" : "No"}
        </span>
      </div>
    );
  };

  const InfoRow = ({ label, value }) => (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-3 border-b last:border-0 ${
        isDark ? "border-slate-700" : "border-gray-100"
      }`}
    >
      <span
        className={`text-sm font-medium ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${
          isDark ? "text-white" : "text-gray-900"
        }`}
      >
        {value ?? "N/A"}
      </span>
    </div>
  );

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Purchase Request Details</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Purchase Request Details</h1>
              <p
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Item information & related purchase orders
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>

            {!isApproved && (
              <button
                onClick={handleApprove}
                disabled={approving || !requestId}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
              >
                <CheckCircle size={16} />
                {approving ? "Approving..." : "Approve PR"}
              </button>
            )}

            {isApproved && (
              <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-green-100 text-green-800">
                <CheckCircle size={16} />
                Approved
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <span className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Item Information Card */}
            <div
              className={`rounded-2xl shadow-xl border overflow-hidden ${
                isDark
                  ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                  : `bg-white ${currentSkin.glass} border-gray-200`
              }`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                  ></div>
                  <Package size={20} className={`text-${baseColor}-600`} />
                  <h3 className="text-xl font-bold">Item Information</h3>
                </div>
              </div>

              <div className="p-6">
                {itemDetails ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                      className={`rounded-xl border p-5 ${
                        isDark
                          ? "bg-slate-800/40 border-slate-700"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <h4
                        className={`text-sm font-bold uppercase tracking-wide mb-4 ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Basic Details
                      </h4>
                      <InfoRow label="Item ID" value={itemDetails.fld_itemid} />
                      <InfoRow label="Item Code" value={itemDetails.fld_itemcode} />
                      <InfoRow label="Item Name" value={itemDetails.fld_itemname} />
                      <InfoRow
                        label="Requested Quantity"
                        value={itemDetails.RequestedQty}
                      />
                      <InfoRow
                        label="PO Quantity"
                        value={itemDetails.POQty ?? "N/A"}
                      />
                      <InfoRow
                        label="PO Price"
                        value={
                          itemDetails.POPrice != null
                            ? `₹${itemDetails.POPrice}`
                            : "N/A"
                        }
                      />
                    </div>

                    <div
                      className={`rounded-xl border p-5 ${
                        isDark
                          ? "bg-slate-800/40 border-slate-700"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <h4
                        className={`text-sm font-bold uppercase tracking-wide mb-4 ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Status Flags
                      </h4>
                      <div className="space-y-3">
                        <StatusBadge
                          label="Prior Purchase Exists"
                          value={itemDetails.PriorPurchaseExists}
                        />
                        <StatusBadge
                          label="PO Exists"
                          value={itemDetails.POExists}
                        />
                        <StatusBadge
                          label="GRN Exists"
                          value={itemDetails.GRNExists}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Info size={40} className="text-gray-400 mb-3" />
                    <p className="text-red-500 font-medium">
                      No item details found.
                    </p>
                    {(!requestId || !itemId) && (
                      <p className="text-sm text-gray-500 mt-1">
                        Missing requestId or itemId. Please navigate from the
                        listing page.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Purchase Orders Card */}
            <div
              className={`rounded-2xl shadow-xl border overflow-hidden ${
                isDark
                  ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                  : `bg-white ${currentSkin.glass} border-gray-200`
              }`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                  ></div>
                  <FileText size={20} className={`text-${baseColor}-600`} />
                  <h3 className="text-xl font-bold">Purchase Orders</h3>
                  {poDetails.length > 0 && (
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {poDetails.length} order
                      {poDetails.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>

              <div className="p-6">
                {poDetails.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {poDetails.map((po, index) => (
                      <div
                        key={index}
                        className={`rounded-xl border p-5 transition hover:shadow-md ${
                          isDark
                            ? "bg-slate-800/40 border-slate-700 hover:border-slate-600"
                            : "bg-white border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div
                            className={`flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600`}
                          >
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {po.fld_branchwiseno || "—"}
                          </span>
                        </div>

                        <h4
                          className={`font-bold text-base mb-3 ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {po.fld_suppliername || "Unknown Supplier"}
                        </h4>

                        <div className="space-y-2.5">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar
                              size={14}
                              className={
                                isDark ? "text-gray-400" : "text-gray-500"
                              }
                            />
                            <span
                              className={
                                isDark ? "text-gray-400" : "text-gray-500"
                              }
                            >
                              PO Date:
                            </span>
                            <span
                              className={`font-medium ${
                                isDark ? "text-white" : "text-gray-800"
                              }`}
                            >
                              {po.fld_Date || "—"}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-sm">
                            <Calendar
                              size={14}
                              className={
                                isDark ? "text-gray-400" : "text-gray-500"
                              }
                            />
                            <span
                              className={
                                isDark ? "text-gray-400" : "text-gray-500"
                              }
                            >
                              Due Date:
                            </span>
                            <span
                              className={`font-medium ${
                                isDark ? "text-white" : "text-gray-800"
                              }`}
                            >
                              {po.fld_DueDate || "—"}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-sm">
                            <Hash
                              size={14}
                              className={
                                isDark ? "text-gray-400" : "text-gray-500"
                              }
                            />
                            <span
                              className={
                                isDark ? "text-gray-400" : "text-gray-500"
                              }
                            >
                              PO Number:
                            </span>
                            <span
                              className={`font-medium ${
                                isDark ? "text-white" : "text-gray-800"
                              }`}
                            >
                              {po.fld_branchwiseno || "—"}
                            </span>
                          </div>

                          <div
                            className={`flex items-center gap-2 text-sm pt-3 mt-3 border-t ${
                              isDark ? "border-slate-700" : "border-gray-100"
                            }`}
                          >
                            <IndianRupee
                              size={14}
                              className={`text-${baseColor}-600`}
                            />
                            <span
                              className={
                                isDark ? "text-gray-400" : "text-gray-500"
                              }
                            >
                              Amount:
                            </span>
                            <span
                              className={`font-bold text-base text-${baseColor}-600`}
                            >
                              ₹{po.Amount ?? "0"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <FileText size={40} className="text-gray-400 mb-3" />
                    <p className="text-gray-500 font-medium">
                      No purchase orders available.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseDetails;